/**
 * 收件人選擇管理 Hook
 * 管理選中的群組和成員，處理收件人新增邏輯
 */

import { useState, useCallback } from 'react';
import { getAdapter } from '../adapters/adapterFactory';

export const useRecipients = () => {
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [selectedMembers, setSelectedMembers] = useState(new Set());

  /**
   * 選擇/取消選擇群組
   */
  const toggleGroup = useCallback((groupId, isChecked) => {
    setSelectedGroups(prev => {
      const newSelection = new Set(prev);
      if (isChecked) {
        newSelection.add(groupId);
      } else {
        newSelection.delete(groupId);
      }
      return newSelection;
    });
  }, []);

  /**
   * 選擇/取消選擇成員
   */
  const toggleMember = useCallback((memberId, isChecked) => {
    setSelectedMembers(prev => {
      const newSelection = new Set(prev);
      if (isChecked) {
        newSelection.add(memberId);
      } else {
        newSelection.delete(memberId);
      }
      return newSelection;
    });
  }, []);

  /**
   * 清除所有選擇
   */
  const clearSelection = useCallback(() => {
    setSelectedGroups(new Set());
    setSelectedMembers(new Set());
  }, []);

  /**
   * 新增收件人
   * @param {Array} groups - 選中的群組列表
   * @param {Object} groupMembers - 群組成員映射 { [nodePath]: [members] }
   * @param {string} messageType - 類型："to" | "cc" | "bcc"
   */
  const addRecipients = useCallback(async (groups, groupMembers, messageType) => {
    // 收集所有選中的成員
    const memberToAdd = [];
    Object.values(groupMembers).flat().forEach(m => {
      if (selectedMembers.has(m.id)) {
        memberToAdd.push(m);
      }
    });

    // 合併群組和成員
    const itemsToAdd = [...groups, ...memberToAdd];

    // 轉換為收件人格式
    const recipients = itemsToAdd
      .map((item) => ({
        displayName: item.displayName,
        emailAddress: item.mail || item.userPrincipalName,
      }))
      .filter((r) => r.emailAddress);

    // 去重
    const uniqueRecipientMap = new Map();
    recipients.forEach((r) => {
      const email = r.emailAddress;
      if (!uniqueRecipientMap.has(email)) {
        uniqueRecipientMap.set(email, r);
      }
    });

    const uniqueRecipients = Array.from(uniqueRecipientMap.values());

    if (uniqueRecipients.length === 0) {
      console.warn("No valid recipients found.");
      return;
    }

    // 使用適配器新增收件人
    try {
      const adapter = getAdapter();
      await adapter.addRecipients(uniqueRecipients, messageType);
    } catch (error) {
      console.error("Failed to add recipients:", error);
      throw error;
    }
  }, [selectedMembers]);


  
  /**
   * 檢查是否有選中的項目
   */
  const hasSelection = useCallback(() => {
    return selectedGroups.size > 0 || selectedMembers.size > 0;
  }, [selectedGroups, selectedMembers]);

  return {
    selectedGroups,
    selectedMembers,
    toggleGroup,
    toggleMember,
    clearSelection,
    addRecipients,
    hasSelection,
  };
};

